import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { SEOHead } from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  CheckCircle2, Star, ShoppingCart, Phone, Shield, Zap,
  ChevronLeft, ChevronRight, Package, ArrowLeft, DollarSign,
  MessageSquare, Heart, Share2, Expand
} from 'lucide-react';

const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({});
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', slug],
    queryFn: async () => {
      const { data, error } = await pmsFrom('products')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const { data: category } = useQuery({
    queryKey: ['product-category', product?.category_id],
    queryFn: async () => {
      if (!product?.category_id) return null;
      const { data, error } = await pmsFrom('product_categories')
        .select('*')
        .eq('id', product.category_id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!product?.category_id,
  });

  const { data: relatedProducts = [] } = useQuery({
    queryKey: ['related-products', product?.category_id, product?.id],
    queryFn: async () => {
      if (!product?.category_id) return [];
      const { data, error } = await pmsFrom('products')
        .select('*')
        .eq('is_active', true)
        .eq('category_id', product.category_id)
        .neq('id', product.id)
        .limit(4);
      if (error) throw error;
      return data;
    },
    enabled: !!product?.category_id,
  });

  const { data: featuredProducts = [] } = useQuery({
    queryKey: ['featured-products-sidebar', product?.id],
    queryFn: async () => {
      const { data, error } = await pmsFrom('products')
        .select('*')
        .eq('is_active', true)
        .eq('is_featured', true)
        .neq('id', product?.id || '')
        .limit(4);
      if (error) throw error;
      return data;
    },
    enabled: !!product,
  });

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!product) return <div className="min-h-screen flex flex-col items-center justify-center text-muted-foreground"><Package className="h-8 w-8 mb-2 opacity-30" /><p>Product not found</p></div>;

  const images = (Array.isArray(product.images) ? product.images : []).map((img: any) => typeof img === 'string' ? img : img?.src || '').filter(Boolean) as string[];
  const features = (product.features as string[] | null) || [];
  const specifications = (product.specifications as Record<string, string> | null) || {};
  const variants = (Array.isArray(product.variants) ? product.variants : []) as { name: string; options: string[]; price_modifier?: number }[];

  const prevImage = () => setSelectedImage(i => i > 0 ? i - 1 : images.length - 1);
  const nextImage = () => setSelectedImage(i => i < images.length - 1 ? i + 1 : 0);

  return (
    <>
      <SEOHead
        title={product.meta_title || product.name}
        description={product.meta_description || product.short_description || ''}
      />

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
          <button onClick={e => { e.stopPropagation(); prevImage(); }} className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"><ChevronLeft className="h-6 w-6 text-white" /></button>
          <img src={images[selectedImage]} alt={product.name} className="max-h-[90vh] max-w-[90vw] object-contain" onClick={e => e.stopPropagation()} />
          <button onClick={e => { e.stopPropagation(); nextImage(); }} className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"><ChevronRight className="h-6 w-6 text-white" /></button>
          <button onClick={() => setLightboxOpen(false)} className="absolute top-4 right-4 text-white/70 hover:text-white text-2xl">&times;</button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">{selectedImage + 1} / {images.length}</div>
        </div>
      )}

      <div className="min-h-screen">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 py-4">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/products" className="hover:text-foreground transition-colors flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" />Products
            </Link>
            {category && (
              <>
                <span>/</span>
                <span>{category.name}</span>
              </>
            )}
            <span>/</span>
            <span className="text-foreground font-medium truncate">{product.name}</span>
          </nav>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 pb-16">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Image Gallery — 5 cols */}
            <div className="lg:col-span-5 space-y-3">
              {/* Featured image */}
              <div className="relative aspect-square rounded-xl overflow-hidden bg-muted border border-border group cursor-pointer" onClick={() => images.length > 0 && setLightboxOpen(true)}>
                {images.length > 0 ? (
                  <>
                    <img src={images[selectedImage]} alt={product.name} className="h-full w-full object-cover" />
                    {images.length > 1 && (
                      <>
                        <button onClick={e => { e.stopPropagation(); prevImage(); }} className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-background/80 backdrop-blur flex items-center justify-center hover:bg-background transition-colors"><ChevronLeft className="h-5 w-5" /></button>
                        <button onClick={e => { e.stopPropagation(); nextImage(); }} className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-background/80 backdrop-blur flex items-center justify-center hover:bg-background transition-colors"><ChevronRight className="h-5 w-5" /></button>
                      </>
                    )}
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity"><div className="h-8 w-8 rounded-full bg-background/80 flex items-center justify-center"><Expand className="h-4 w-4" /></div></div>
                  </>
                ) : (
                  <div className="h-full w-full flex items-center justify-center"><Package className="h-16 w-16 text-muted-foreground/20" /></div>
                )}
                {product.is_featured && <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground gap-1"><Star className="h-3 w-3 fill-current" />Featured</Badge>}
                {product.compare_at_price && product.price && Number(product.price) < Number(product.compare_at_price) && (
                  <Badge variant="destructive" className="absolute top-4 right-4">
                    {Math.round((1 - Number(product.price) / Number(product.compare_at_price)) * 100)}% OFF
                  </Badge>
                )}
              </div>

              {/* Thumbnail gallery */}
              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      className={`h-16 w-16 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-colors ${i === selectedImage ? 'border-primary' : 'border-transparent hover:border-muted-foreground/30'}`}
                    >
                      <img src={img} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info — 7 cols */}
            <div className="lg:col-span-7 space-y-5">
              <div>
                {category && <span className="text-sm text-primary font-medium">{category.name}</span>}
                <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mt-1">{product.name}</h1>
                {product.brand && (
                  <p className="text-muted-foreground mt-1">{product.brand}{product.model_number ? ` • Model: ${product.model_number}` : ''}</p>
                )}
              </div>

              {/* Pricing */}
              <div className="bg-muted/50 rounded-xl p-5 border border-border space-y-3">
                {product.price != null && (
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl font-bold text-foreground">${Number(product.price).toLocaleString()}</span>
                    {product.compare_at_price != null && (
                      <span className="text-lg text-muted-foreground line-through">${Number(product.compare_at_price).toLocaleString()}</span>
                    )}
                    {product.compare_at_price != null && product.price != null && (
                      <Badge variant="secondary" className="text-primary">
                        Save ${(Number(product.compare_at_price) - Number(product.price)).toLocaleString()}
                      </Badge>
                    )}
                  </div>
                )}
                {product.financing_available && product.monthly_price != null && (
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-center gap-3">
                    <DollarSign className="h-5 w-5 text-primary flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        As low as <span className="text-primary font-bold">${Number(product.monthly_price)}/mo</span>
                        {product.financing_term_months && <span className="text-muted-foreground"> for {product.financing_term_months} months</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">Financing available with approved credit</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Stock & SKU */}
              <div className="flex items-center gap-2 flex-wrap">
                {product.in_stock ? (
                  <Badge variant="default" className="bg-primary/10 text-primary border-primary/20"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />In Stock</Badge>
                ) : (
                  <Badge variant="destructive">Out of Stock</Badge>
                )}
                {product.efficiency_rating && <Badge variant="secondary"><Zap className="h-3 w-3 mr-1" />{product.efficiency_rating}</Badge>}
                {product.sku && <span className="text-xs text-muted-foreground">SKU: {product.sku}</span>}
              </div>

              {product.short_description && <p className="text-muted-foreground leading-relaxed">{product.short_description}</p>}

              {/* Variant Selectors */}
              {variants.length > 0 && (
                <div className="space-y-3 bg-muted/30 rounded-lg p-4 border border-border">
                  <p className="text-sm font-medium text-foreground">Options</p>
                  {variants.map((v, i) => (
                    <div key={i} className="grid grid-cols-[120px_1fr] items-center gap-3">
                      <Label className="text-sm">{v.name}</Label>
                      <Select value={selectedVariants[v.name] || ''} onValueChange={val => setSelectedVariants(prev => ({ ...prev, [v.name]: val }))}>
                        <SelectTrigger className="h-9"><SelectValue placeholder={`Select ${v.name}`} /></SelectTrigger>
                        <SelectContent>
                          {(v.options || []).map((opt, oi) => (
                            <SelectItem key={oi} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                {product.checkout_url && (
                  <Button size="lg" className="gap-2 flex-1" asChild>
                    <a href={product.checkout_url} target="_blank" rel="noopener noreferrer">
                      <ShoppingCart className="h-4 w-4" />Buy Now
                    </a>
                  </Button>
                )}
                <Button size="lg" variant={product.checkout_url ? 'outline' : 'default'} className="gap-2 flex-1" asChild>
                  <Link to={`/contact?product=${encodeURIComponent(product.name)}`}>
                    <MessageSquare className="h-4 w-4" />Request a Quote
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="gap-2" asChild>
                  <a href="tel:+15552345678"><Phone className="h-4 w-4" />Call Now</a>
                </Button>
              </div>

              {/* Trust Signals */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Shield, label: 'Professional Installation' },
                  { icon: Star, label: 'Licensed & Insured' },
                  { icon: CheckCircle2, label: product.warranty_info || 'Warranty Included' },
                ].map((t, i) => (
                  <div key={i} className="flex flex-col items-center text-center gap-1.5 p-3 rounded-lg bg-muted/50">
                    <t.icon className="h-5 w-5 text-primary" />
                    <span className="text-xs text-muted-foreground leading-tight">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs: Description, Features, Specs */}
          <div className="mt-12">
            <Tabs defaultValue="description">
              <TabsList>
                <TabsTrigger value="description">Description</TabsTrigger>
                {features.length > 0 && <TabsTrigger value="features">Features</TabsTrigger>}
                {Object.keys(specifications).length > 0 && <TabsTrigger value="specifications">Specifications</TabsTrigger>}
              </TabsList>

              <TabsContent value="description" className="mt-6">
                {product.description ? (
                  <div className="prose prose-neutral dark:prose-invert max-w-none text-muted-foreground leading-relaxed whitespace-pre-wrap">{product.description}</div>
                ) : (
                  <p className="text-muted-foreground">No detailed description available.</p>
                )}
              </TabsContent>

              {features.length > 0 && (
                <TabsContent value="features" className="mt-6">
                  <div className="grid sm:grid-cols-2 gap-3">
                    {features.map((f, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground">{f}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}

              {Object.keys(specifications).length > 0 && (
                <TabsContent value="specifications" className="mt-6">
                  <div className="rounded-xl border border-border overflow-hidden">
                    {Object.entries(specifications).map(([key, val], i) => (
                      <div key={key} className={`flex ${i % 2 === 0 ? 'bg-muted/30' : ''}`}>
                        <span className="w-1/3 px-4 py-3 text-sm font-medium text-foreground border-r border-border">{key}</span>
                        <span className="flex-1 px-4 py-3 text-sm text-muted-foreground">{val}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </div>

          {/* Featured Products */}
          {featuredProducts.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                <Star className="h-5 w-5 text-primary" />Featured Products
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {featuredProducts.map(fp => {
                  const fpImages = (Array.isArray(fp.images) ? fp.images : []).map((img: any) => typeof img === 'string' ? img : img?.src || '').filter(Boolean);
                  return (
                    <Link key={fp.id} to={`/products/${fp.slug}`} className="group rounded-xl border border-border bg-card hover:border-primary/40 transition-all overflow-hidden">
                      <div className="aspect-square bg-muted overflow-hidden relative">
                        {fpImages[0] ? <img src={fpImages[0]} alt={fp.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="h-full w-full flex items-center justify-center"><Package className="h-8 w-8 text-muted-foreground/20" /></div>}
                        {fp.is_featured && <Badge className="absolute top-2 left-2 text-[10px]">Featured</Badge>}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">{fp.name}</h3>
                        {fp.price != null && <p className="text-lg font-bold text-foreground mt-1">${Number(fp.price).toLocaleString()}</p>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-foreground mb-6">You Might Also Like</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map(rp => {
                  const rpImages = (Array.isArray(rp.images) ? rp.images : []).map((img: any) => typeof img === 'string' ? img : img?.src || '').filter(Boolean);
                  return (
                    <Link key={rp.id} to={`/products/${rp.slug}`} className="group rounded-xl border border-border bg-card hover:border-primary/40 transition-all overflow-hidden">
                      <div className="aspect-square bg-muted overflow-hidden">
                        {rpImages[0] ? <img src={rpImages[0]} alt={rp.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="h-full w-full flex items-center justify-center"><Package className="h-8 w-8 text-muted-foreground/20" /></div>}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">{rp.name}</h3>
                        {rp.price != null && <p className="text-lg font-bold text-foreground mt-1">${Number(rp.price).toLocaleString()}</p>}
                        {rp.brand && <p className="text-xs text-muted-foreground mt-1">{rp.brand}</p>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductDetailPage;
